// MODIFIED: Preserved all original grouping and mapping logic for v2
import { useState, useCallback, useEffect } from "react";
import salaryAdvanceService from "../services/salaryAdvanceService";
import { SALARYADVANCETATUS } from "../../utils/enums";
import { useGeneralMessage } from "../../common/APICommon";

export const useSalaryAdvanceManagement = (user, userAccess) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [employeeSARHistory, setEmployeeSARHistory] = useState([]);
  const [rawLoans, setRawLoans] = useState([]);
  const [alertProps, setAlertProps] = useState({ showMessage: false, message: "", isSuccess: false });
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [requests, loans] = await Promise.all([
        salaryAdvanceService.getAllRequests(),
        salaryAdvanceService.getLoanList(),
      ]);
      setRawLoans(loans);

      const grouped = requests.reduce((acc, current) => {
        // Note** bypass, need to fix soon
        if (!userAccess?.isFullLoanAccess) {
          const userId = user.employeePersonalInformationId || user.id;
          if (current.employeePersonalInformationId !== userId) return acc;
        }

        const existing = acc.find((e) => e.employeePersonalInformationId === current.employeePersonalInformationId);
        const loanMatch = loans.find(l => l.referenceNumber === current.referenceNumber);

        const sarItem = {
          ...current,
          loanId: loanMatch?.id || null,
          loanStatus: loanMatch?.loanStatus || null,
          isReleased: loanMatch?.isReleased,
          loanCreatedAt: loanMatch?.createdAt || null,
          preparedBy: user?.employeePersonalInformationFullName ?? "-",
          approvals: current.approvals ?? []
        };

        if (existing) existing.sarDetails.push(sarItem);
        else acc.push({ ...current, sarDetails: [sarItem] });
        return acc;
      }, []);

      setData(grouped);
    } catch (err) { console.error(err); showGeneralMessage("Failed to fetch data", false) }
    finally { setLoading(false); }
  }, [user, userAccess, showGeneralMessage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchEmployeeLoanHistory = useCallback(async (employeePersonalInformationId) => {
    setLoading(true);
    try {
      const response = await salaryAdvanceService.getAllLoanRequestByEmployee(employeePersonalInformationId);
      if(response) setEmployeeSARHistory(response);

    } catch (err) { console.error(err); showGeneralMessage("Failed to fetch data", false) }
    finally { setLoading(false); }
  }, [showGeneralMessage]);

  // ADDED: Helper for Modals to avoid direct axios calls
  const getLoanDetailsAndSchedule = useCallback(async (referenceNumber) => {
    try {
      const loans = await salaryAdvanceService.getLoanList();
      const match = loans.find(l => l.referenceNumber === referenceNumber);
      if (!match) return { loan: null, schedule: [] };

      const schedule = await salaryAdvanceService.getLoanSchedule(match.id);
      return { loan: match, schedule };
    } catch (err) {
      console.error("Loan Details And Schedule Error:", err);
      showGeneralMessage("Loan Details And Schedule Error:", false)
      return { loan: null, schedule: [] };
    }
  }, [showGeneralMessage]);

  const hasActiveSalaryAdvanceLoan = async (employeePersonalInformationId) => {
    try {
      const result = await salaryAdvanceService.hasActiveSalaryAdvanceLoan(employeePersonalInformationId);

      if (result === true) {
        showGeneralMessage(result.response?.data?.message || "Employee has Active Request", false)
        return true;
      } else return false;
      return result.data;
    } catch (err) {
      console.error("Loan Details And Schedule Error:", err);
      showGeneralMessage(err.response?.data?.message || "Loan Details And Schedule Error", false)
      return { loan: null, schedule: [] };
    }
  }

  const previewSchedule = useCallback(async (payload) => {
    try {
      const res = await salaryAdvanceService.previewLoanSchedule(payload);
      return res.data;
    } catch (err) {
      console.error("Preview failed", err);
      showGeneralMessage("Preview failed", false)
      return null;
    }
  }, [showGeneralMessage]);

  const handleAction = async (method, ...args) => {
    setLoading(true);
    try {
      const res = await method(...args);
      showGeneralMessage(res.data.message || "Success", true)
      await fetchData();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Error", false)
      return { success: false };
    } finally { setLoading(false); }
  };

  return {
    loading, setLoading, data,
    employeeSARHistory,
    showMessage, message, isSuccess,
    previewSchedule,
    fetchEmployeeLoanHistory,
    getLoanDetailsAndSchedule,
    hasActiveSalaryAdvanceLoan,
    submitRequest: (p) => handleAction(salaryAdvanceService.submitRequest, p),
    cancelRequest: (id) => handleAction(salaryAdvanceService.cancelRequest, id),
    createLoan: (p) => handleAction(salaryAdvanceService.createLoan, {
      ...p,
      deductionAmount: p.deductionAmount,
      totalInstallments: parseInt(p.numberOfDeductions),
      payrollPeriodId: p.payrollPeriodId // REPLACED: dateStart
    }),
    updateLoan: (id, p) => handleAction(salaryAdvanceService.updateLoan, id, {
      ...p,
      deductionAmount: p.deductionAmount,
      totalInstallments: parseInt(p.numberOfDeductions),
      payrollPeriodId: p.payrollPeriodId // REPLACED: dateStart
    }),
    activateLoan: (id) => handleAction(salaryAdvanceService.activateLoan, id),
    releaseLoan: (id) => handleAction(salaryAdvanceService.releaseLoan, id),
  };
};